/**
 * SkillIconRegistry — maps each skillId to its spritesheet frame at startup.
 *
 * For each tree, active and evolution nodes are assigned frame indices in their
 * order of appearance: foundation first (frame 0), then all active/evolution
 * entries from the nodes array in array order.
 *
 * Usage:
 *   SkillIconRegistry.init(ConfigLoader.v4Trees);
 *   const entry = SkillIconRegistry.get('stab'); // { treeName: 'blades', frameIndex: 0 }
 */
import type { V4TreeDefinition } from '../utils/Types';

export interface SkillIconEntry {
  treeName: string;
  frameIndex: number;
}

const FALLBACK: SkillIconEntry = { treeName: 'blades', frameIndex: 0 };

export class SkillIconRegistry {
  private static _map: Map<string, SkillIconEntry> = new Map();
  private static _initialized = false;

  /** Build the registry from all tree definitions. Call once at startup. */
  static init(trees: Record<string, V4TreeDefinition>): void {
    if (this._initialized) return;
    this._map.clear();

    for (const [treeName, tree] of Object.entries(trees)) {
      let frameIndex = 0;

      // Foundation is always active — it occupies frame 0
      this._map.set(tree.foundation.id, { treeName, frameIndex });
      frameIndex++;

      // Active and evolution nodes in array order fill subsequent frames
      for (const node of tree.nodes) {
        if (node.nodeType === 'active' || node.nodeType === 'evolution') {
          this._map.set(node.id, { treeName, frameIndex });
          frameIndex++;
        }
      }
    }

    this._initialized = true;
  }

  /** Look up a skill's icon entry. Returns the blades frame-0 fallback for unknown IDs. */
  static get(skillId: string): SkillIconEntry {
    return this._map.get(skillId) ?? FALLBACK;
  }
}
